
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createBulkExpenses, createExpense, deleteExpense, getCategorySummary, getEmployeeExpenses, getExpenseByCategory, getExpenses, getExpensesCategory, updateExpense } from "../lib/expenceApi";

// ✅ Hook for single expense
export const useCreateExpense = () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: createExpense,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["expenses"] });
      },
      onError: (error) => {
        console.error("Expense creation failed:", error);
      },
    });
  };
  
  // ✅ Hook for bulk expenses
  export const useCreateBulkExpenses = () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: createBulkExpenses,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["expenses"] });
      },
      onError: (error) => {
        console.error("Bulk expense creation failed:", error);
      },
    });
  };

  export const useGetExpenses = (filters = {},options = {}) => {
    const {
      employeeId = "",
      page = 1,
      limit = 10,
      categoryId = "",
      startDate = "",
      endDate = "",
      status = "",
      search = "",
    } = filters;
  
    return useQuery({
      // ✅ Use only primitive values for stability
      queryKey: [
        "expenses",
        employeeId,
        page,
        limit,
        categoryId,
        startDate,
        endDate,
        status,
        search,
      ],
      queryFn: () =>
        getExpenses({
          employeeId,
          page,
          limit,
          categoryId,
          startDate,
          endDate,
          status,
          search,
        }),
      keepPreviousData: true,
      staleTime: 1000 * 60 * 2, // 2 min caching
      enabled: options.enabled ?? true,
    });
  };
  



  export const useGetExpensesCategory = () => {
    return useQuery({
      queryKey: ["categoryExpense"],
      queryFn: getExpensesCategory,
      keepPreviousData: true,
    });
  };
  
  
// 🔹 Update Expense Mutation Hook
export const useUpdateExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateExpense,
    onSuccess: (data) => {
      queryClient.setQueryData(["expenses"], old => ({
        ...old,
        expenses: old?.expenses?.map(e => e._id === data._id ? data : e)
      }));
      queryClient.invalidateQueries({ queryKey: ["expenses"] }); // refresh data
    },
  });
};



export const useGetEmployeeExpenses = (filters = {}, options = {}) => {
  const {
    page = 1,
    limit = 10,
    startDate = "",
    endDate = "",
    search = "",  // ✅ This receives the search
  } = filters;

  return useQuery({
    queryKey: [
      "employee-expenses",
      page,
      limit,
      startDate,
      endDate,
      search,  // ✅ Add this to queryKey for proper caching
    ],
    queryFn: () =>
      getEmployeeExpenses({
        page,
        limit,
        startDate,
        endDate,
        search,  // ✅ Pass search to API function
      }),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 2,
    enabled: options.enabled ?? true,
  });
};


// React Query hook
export const useGetExpenseByCategory = (filters = {}, options = {}) => {
  const {
    categoryId = "",
    page = 1,
    limit = 10,
    startDate = "",
    endDate = "",
    search = "",
  } = filters;

  return useQuery({
    queryKey: [
      "expenseByCategory",
      categoryId,
      page,
      limit,
      startDate,
      endDate,
      search,
    ],
    queryFn: () =>
      getExpenseByCategory({
        categoryId,
        page,
        limit,
        startDate,
        endDate,
        search,
      }),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 2, // 2 min caching
    enabled: options.enabled ?? false, // ✅ Disabled by default, enable only when category is clicked
  });
};


// React Query hook
export const useGetCategorySummary = (filters = {}, options = {}) => {
  const {
    page = 1,
    limit = 10,
    startDate = "",
    endDate = "",
    search = "",
  } = filters;

  return useQuery({
    queryKey: [
      "categorySummary",
      page,
      limit,
      startDate,
      endDate,
      search,
    ],
    queryFn: () =>
      getCategorySummary({
        page,
        limit,
        startDate,
        endDate,
        search,
      }),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 2,
    enabled: options.enabled ?? true,
  });
};

export const useDeleteExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (expenseId) => deleteExpense(expenseId),
    onSuccess: () => {
      // 🔄 Invalidate expense list to refetch updated data
      queryClient.invalidateQueries({ queryKey: ["expenses", "list"] });
    },
    onError: (error) => {
      console.error("❌ Failed to delete expense:", error);
    },
  });
};